import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "../i18n";
import {
  DEFAULT_LOCALE,
  getLocaleFromPathname,
  localizePath,
} from "../utils/localeRouting";

const codeLabels = {
  en: "English",
  zh: "简体中文",
  "zh-TW": "繁體中文",
  fr: "Français",
  es: "Español",
  ar: "العربية",
  ja: "日本語",
  ru: "Русский",
  ko: "한국어",
  pt: "Português",
  "pt-BR": "Português (Brasil)",
  it: "Italiano",
  de: "Deutsch",
  hi: "हिन्दी",
  mn: "Монгол",
  th: "ไทย",
  uk: "Українська",
  vi: "Tiếng Việt",
  id: "Bahasa Indonesia",
  tl: "Tagalog",
  bn: "বাংলা",
};

function LanguageSwitcher({ className = "" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentLocale =
    getLocaleFromPathname(location.pathname) ||
    i18n.resolvedLanguage ||
    i18n.language ||
    DEFAULT_LOCALE;
  const [current, setCurrent] = useState(currentLocale);

  useEffect(() => {
    setCurrent(currentLocale);
  }, [currentLocale]);

  useEffect(() => {
    const handler = (lng) => setCurrent(lng || "en");
    i18n.on("languageChanged", handler);
    return () => {
      i18n.off("languageChanged", handler);
    };
  }, []);

  const options = useMemo(() => {
    // ensure only codes that i18n supports are shown
    const supportedCodes = Object.keys(i18n?.options?.resources || codeLabels);
    return supportedCodes
      .filter((code) => codeLabels[code])
      .map((code) => ({ code, label: codeLabels[code] }));
  }, []);

  const handleChange = (e) => {
    const lang = e.target.value;
    setCurrent(lang);
    try {
      i18n.changeLanguage(lang);
      if (typeof window !== "undefined") {
        localStorage.setItem("i18n_lang", lang);
        localStorage.setItem("i18n_lang_set_by_user", "1");
      }
    } catch (_) {}
    navigate(
      localizePath(
        `${location.pathname}${location.search}${location.hash}`,
        lang
      )
    );
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <label htmlFor="language-switcher" className="sr-only">
        Language
      </label>
      <select
        id="language-switcher"
        value={current}
        onChange={handleChange}
        className="h-10 appearance-none px-4 pr-12 rounded-lg bg-neutral-800 text-neutral-100 border border-neutral-600 text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-neutral-500 min-w-[160px]"
        aria-label="Language"
      >
        {options.map((opt) => (
          <option key={opt.code} value={opt.code}>
            {opt.label}
          </option>
        ))}
      </select>
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-300"
        focusable="false"
      >
        <path
          d="M6 8l4 4 4-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

export default LanguageSwitcher;
